import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, Bot, Building2, CalendarClock, Camera, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, CreditCard, FileText, Gavel, Globe, HelpCircle, KanbanSquare, LayoutDashboard, LogOut, Megaphone, Menu, Moon, PieChart, Receipt, Settings, Shield, ShoppingBag, Sparkles, Sun, Users, Warehouse, Wrench } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFacility } from '../context/FacilityContext';
import { useTheme } from '../context/ThemeContext';
import apiClient from '../utils/apiClient';
import CommandPalette from './CommandPalette';
import ImpersonationBanner from './ImpersonationBanner';
import { toast } from 'sonner';

// Collapsed-by-default icon rail width and expanded width. A 52px icon
// slab that opens to 210px only when the operator clicks the › toggle at
// its foot (owner, 2026-10-25 — it used to open on hover, which made the
// page jump whenever the mouse crossed the rail). The choice is remembered
// per browser. Expanding PUSHES the content right (in flex flow) with a
// 180ms width transition.
const RAIL_W_COLLAPSED = 52;
const RAIL_W_EXPANDED = 210;
const RAIL_TRANSITION = 'width 180ms ease-out';
const RAIL_PREF_KEY = 'mysms360.rail.expanded';

const readRailPref = () => {
  try {
    return window.localStorage.getItem(RAIL_PREF_KEY) === '1';
  } catch {
    return false;
  }
};

// Brand color trio for the user-menu dot picker. Hard-coded swatches
// (not pulled from CSS vars) because each button must show its OWN
// brand color regardless of the currently active brand.
const BRAND_SWATCHES = {
  blue: '#2563EB',
  green: '#16A34A',
  orange: '#EA580C',
};
const VALID_BRAND_KEYS = ['blue', 'green', 'orange'];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState(readRailPref);
  const toggleRail = () => {
    setRailExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(RAIL_PREF_KEY, next ? '1' : '0');
      } catch { /* private window: the choice simply isn't remembered */ }
      return next;
    });
  };
  // Embed-shell mode: the native mobile app renders dashboard pages inside
  // an in-app WebView and provides its own tabs, back chrome, and facility
  // picker — so the web sidebar, header, and command palette hide and the
  // page content is the whole surface. The app injects the flag into
  // localStorage before the page boots; browsers never set it.
  const embedShell = (() => {
    try {
      return localStorage.getItem('embedShell') === '1';
    } catch {
      return false;
    }
  })();

  // Stale-bundle detector. A long-lived tab (the phone at the counter)
  // keeps executing OLD JavaScript for days — on 2026-08-02 an auth fix
  // was live on Vercel while the owner's phone still showed the retired
  // dead-end error from the previous bundle. Poll the served HTML for
  // the current bundle hash; when it changes, offer a one-tap reload.
  useEffect(() => {
    const current = document
      .querySelector('script[src*="/static/js/main."]')
      ?.getAttribute('src');
    if (!current) return undefined;
    let cancelled = false;
    let notified = false;
    const check = async () => {
      if (cancelled || notified) return;
      try {
        const res = await fetch('/', { cache: 'no-store' });
        const html = await res.text();
        const m = html.match(/\/static\/js\/main\.[a-z0-9]+\.js/);
        if (!cancelled && m && m[0] !== current) {
          notified = true;
          toast.info('A new version of mySMS360 is available.', {
            duration: Infinity,
            action: {
              label: 'Reload',
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        /* offline / transient — try again next tick */
      }
    };
    const id = setInterval(check, 15 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
  // Custom dropdown open state for the facility picker. Replaced the
  // native <select> 2026-06-05 because operators reported the select
  // didn't fire onChange reliably (Vercel deploy + Windows Chrome).
  // A button + absolute-positioned menu sidesteps any native-select
  // event quirks and gives us explicit control over click->state.
  const [facilityPickerOpen, setFacilityPickerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isManager, isPlatformUser, isPortfolioOwner, impersonation } = useAuth();
  const { facilities, currentFacility, switchFacility } = useFacility();
  const facilityId = currentFacility?._id;
  // Unread inbound SMS count -> the Messages nav badge. Polled so a freshly
  // arrived text bumps the badge from anywhere in the app (ESS-style).
  const [smsUnread, setSmsUnread] = useState(0);
  useEffect(() => {
    if (!facilityId) { setSmsUnread(0); return undefined; }
    let active = true;
    const fetchUnread = async () => {
      try {
        const res = await apiClient.get(`/api/facilities/${facilityId}/sms/unread-count`);
        if (active) setSmsUnread(res?.data?.unread_count || 0);
      } catch (_) { /* nav badge is best-effort */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { active = false; clearInterval(id); };
  }, [facilityId]);
  const { effective, setTheme, brand, setBrand } = useTheme();
  const isDark = effective === 'dark';

  // Platform users in vendor mode (no impersonation) only see the platform
  // surfaces — the operator nav is hidden until they enter a support session.
  const showOperatorNav = !isPlatformUser() || !!impersonation;

  // Portfolio is only meaningful for portfolio owners (rollup across
  // facilities). Facility-only users (manager/staff/viewer/facility_owner)
  // never see it. Platform users while impersonating get full visibility.
  const showPortfolio = isPortfolioOwner() || (isPlatformUser() && !!impersonation);

  // 52px icon rail (CANONICAL_design_spec.md §Icon rail). Flat list with
  // `sep:true` divider markers. Order (revised 2026-06-05):
  //   Portfolio (if shown), Dashboard, Reports |
  //   Customers, Units, Rental |
  //   Invoices, Payments |
  //   AIVA, Camera Vision |
  //   Website, Marketing, Marketplace |
  //   Auctions.
  // Settings pinned bottom.
  // AIVA + Camera Vision promoted ABOVE the marketing cluster (2026-06-05)
  // so the premium voice + vision pair sits closer to the operational
  // money rows (Invoices/Payments) and the marketing/website cluster
  // forms the public-facing band beneath them. Auctions added as its
  // own bottom band -- delinquency cascade endpoint.
  const railItems = [
    ...(isPlatformUser()
      ? [{ name: 'Platform Admin', path: '/platform-admin', icon: Shield }, { sep: true }]
      : []),
    ...(showOperatorNav
      ? [
          ...(showPortfolio
            ? [{ name: 'Portfolio', path: '/portfolio', icon: PieChart }]
            : []),
          { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
          { name: 'Reports', path: '/reports', icon: BarChart3 },
          // Labeled section headers (owner, 2026-08-14): Operate / Grow /
          // AI replace the bare dividers; Invoices + Payments fold into
          // Operate so the daily-work band is one contiguous group.
          { header: 'Operate' },
          { name: 'Customers', path: '/customers', icon: Users },
          { name: 'Units', path: '/units', icon: Warehouse },
          // Rentals + Leads consolidated into one /rentals page with tabs.
          // Site Map consolidated into /units?view=map tab.
          { name: 'Rental', path: '/rentals', icon: FileText },
          // Reservations was a live page reachable only by URL — surfaced in
          // the rail (audit: hidden features). Quotes was removed 2026-07-19:
          // a price-quote artifact is not how self-storage sells. A prospect
          // gets a rate and a unit hold, not a quotation to shop around with.
          // Lead Management merged in as a tab 2026-08-07 (owner) — the
          // label names both halves.
          { name: 'Leads', path: '/reservations', icon: CalendarClock },
          { name: 'Invoices', path: '/invoices', icon: Receipt },
          { name: 'Payments', path: '/payments', icon: CreditCard },
          { header: 'Grow' },
          // Traditional ops cluster: web presence, marketing channels,
          // vendor sourcing. Sits BEFORE the AI cluster so operators see
          // the familiar-PMS surfaces first.
          { name: 'Website', path: '/website', icon: Globe },
          { name: 'Marketing', path: '/marketing', icon: Megaphone },
          { name: 'Marketplace', path: '/marketplace', icon: ShoppingBag },
          // Auctions moved from Grow (owner, 2026-08-14): the lien/auction
          // engine is an automated workflow, not a growth channel.
          { header: 'AI and Automation' },
          { name: 'Auctions', path: '/auctions', icon: Gavel },
          // AI AGENTS — the agentic wedge vs other PMS. Each entry is one
          // agent with one job, named so an operator can tell what it does
          // without opening it.
          //
          // No separate "AI Analytics Agent" entry: Predictive Intelligence
          // (churn risk, maintenance, demand forecasting) lives INSIDE the
          // chatbot as of 2026-07-21 — those are answers about the facility,
          // which is what that surface is for. Conventional reporting keeps
          // its own "Reports" entry above.
          { name: 'AI Voice Agent (AIVA)', path: '/intelligence', icon: Bot },
          { name: 'AI Camera Agent', path: '/sms-ai', icon: Camera },
          { name: 'AI Facility Chatbot', path: '/assistant', icon: Sparkles },
          { name: 'AI Project Management', path: '/projects', icon: KanbanSquare },
          // Automation moved into Settings (2026-06-03); access via
          // /settings?tab=automation.
        ]
      : []),
  ];

  const isActive = (path) => location.pathname.startsWith(path);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const displayRole = user?.platform_role || user?.role || '';
  const displayRoleLabel = displayRole.replace(/_/g, ' ');

  // Header subline per spec: "{city}, {state} · {N} units · {date}"
  // The date next to the facility's name is the FACILITY's date — the
  // browser's calendar disagrees whenever the operator travels (or the
  // facility's evening has rolled the browser's day already).
  const headerDate = new Date().toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    ...(currentFacility?.timezone ? { timeZone: currentFacility.timezone } : {}),
  });
  const headerSubline = (() => {
    if (isPlatformUser() && !impersonation) return headerDate;
    const f = currentFacility;
    const parts = [];
    if (f?.city && f?.state) parts.push(`${f.city}, ${f.state}`);
    else if (f?.city) parts.push(f.city);
    if (f?.unit_count != null) parts.push(`${f.unit_count} units`);
    parts.push(headerDate);
    return parts.join(' · ');
  })();

  // Public-facing facility short code (SMS-XXXXXX). Backed by
  // facility.facility_external_id; operators surface it on support
  // tickets, invoices, marketing. Rendered as a tiny monospace badge
  // next to the page-level controls so it's selectable but stays
  // out of the way.
  const facilityExternalId = currentFacility?.facility_external_id;
  // Portfolio reference (POR-XXXXXX). One per operator, shared by every
  // facility they run — quoted alongside the facility code on support
  // tickets so both levels are identifiable.
  const portfolioExternalId = currentFacility?.portfolio_external_id;

  // Sidebar row: icon + label. Collapsed mode hides the label (opacity 0,
  // pointer-events still routed). Expanded reveals it with a short fade.
  // Active state = brand-blue tint (matches marketing site).
  const NavRow = ({ item, expanded }) => {
    const Icon = item.icon;
    const active = item.matchSearch
      ? location.search.includes(item.matchSearch)
      : isActive(item.path);
    const badge = item.badge || 0;
    return (
      <Link
        to={item.to || item.path}
        onClick={() => setMobileOpen(false)}
        aria-label={badge > 0 ? `${item.name} (${badge} unread)` : item.name}
        title={expanded ? undefined : item.name}
        className="group relative mx-2 h-8 flex items-center gap-2.5 rounded-lg px-2 transition-colors overflow-hidden hover:bg-white/10"
        style={{ backgroundColor: active ? 'var(--c-rail-active-bg)' : 'transparent' }}
      >
        <span className="relative flex-shrink-0">
          <Icon
            className="h-[16px] w-[16px]"
            strokeWidth={1.75}
            style={{ color: active ? 'var(--c-rail-icon-active)' : 'var(--c-rail-icon)' }}
          />
          {badge > 0 && (
            <span
              className="absolute flex items-center justify-center rounded-full font-semibold text-white"
              style={{
                top: -5, left: 9, minWidth: 15, height: 15, fontSize: 9,
                lineHeight: 1, padding: '0 3px', background: '#DC2626',
                boxShadow: '0 0 0 1.5px var(--c-rail)',
              }}
            >
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        <span
          className="whitespace-nowrap flex-1"
          style={{
            fontSize: '13px',
            fontWeight: active ? 600 : 400,
            color: active ? 'var(--c-rail-icon-active)' : 'var(--c-rail-icon)',
            opacity: expanded ? 1 : 0,
            transition: 'opacity 120ms ease-out',
            pointerEvents: expanded ? 'auto' : 'none',
          }}
        >
          {item.name}
        </span>
        {badge > 0 && expanded && (
          <span
            className="flex items-center justify-center rounded-full font-semibold text-white flex-shrink-0"
            style={{ minWidth: 18, height: 18, fontSize: 10, padding: '0 5px', background: '#DC2626' }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </Link>
    );
  };

  const SidebarContent = ({ expanded }) => (
    <div
      className="h-full flex flex-col items-stretch"
      style={{ backgroundColor: 'var(--c-rail)', borderRight: '0.5px solid var(--c-rail-border)' }}
    >
      {/* Brand wordmark -- only renders when the rail is expanded so the
          collapsed 52px strip stays icon-only. The wordmark
          mirrors the marketing-site lockup: "mySMS" + "360" + TM
          superscript -- but rendered all-white since the rail is solid
          brand color. Fades with the rail expand transition. Tight top
          padding (pt-3 pb-2) so the wordmark sits compactly above the
          nav rows and the rail rhythm stays dense from top to bottom. */}
      <div
        className="flex-shrink-0 px-3 pt-3 pb-2 overflow-hidden whitespace-nowrap"
        style={{
          opacity: expanded ? 1 : 0,
          transition: 'opacity 120ms ease-out',
          pointerEvents: expanded ? 'auto' : 'none',
        }}
        aria-hidden={!expanded}
      >
        <span
          className="font-semibold tracking-tight leading-none select-none"
          style={{ fontSize: '17px', color: '#FFFFFF' }}
        >
          mySMS<span style={{ opacity: 0.88 }}>360</span>
          <span
            className="font-medium"
            style={{
              fontSize: '0.45em',
              verticalAlign: 'super',
              marginLeft: '0.1em',
              lineHeight: 1,
              opacity: 0.7,
            }}
          >
            TM
          </span>
        </span>
      </div>

      {/* Nav rows. flex-1 lets nav consume available space; the items
          themselves stay at their natural height (no stretching) and the
          Settings row sticks to the bottom. gap-0.5 + pt-1/pb-1 keeps
          the rail dense so all 13-ish entries fit without scroll on
          standard laptop heights. */}
      <nav className="flex-1 min-h-0 overflow-y-auto sidebar-scroll pt-1 pb-1 flex flex-col gap-0.5">
        {railItems.map((item, idx) =>
          item.header ? (
            // Section header: uppercase label when the rail is expanded;
            // collapses to the old hairline divider at icon-only width.
            <div key={`hdr-${idx}`} className="flex-shrink-0 mx-3 mt-2.5 mb-0.5">
              {expanded ? (
                <span
                  className="block px-1 select-none whitespace-nowrap"
                  style={{
                    fontSize: '10px', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.92)',
                  }}
                >
                  {item.header}
                </span>
              ) : (
                <div className="h-px" style={{ backgroundColor: 'var(--c-rail-border)' }} />
              )}
            </div>
          ) : item.sep ? (
            <div
              key={`sep-${idx}`}
              className="mx-3 my-1.5 h-px flex-shrink-0"
              style={{ backgroundColor: 'var(--c-rail-border)' }}
            />
          ) : (
            <NavRow key={item.path} item={item} expanded={expanded} />
          )
        )}
      </nav>

      {/* Settings pinned bottom */}
      {isManager() && (
        <div
          className="py-2 flex-shrink-0"
          style={{ borderTop: '0.5px solid var(--c-rail-border)' }}
        >
          <NavRow item={{ name: 'Settings', path: '/settings', icon: Settings, badge: smsUnread }} expanded={expanded} />
        </div>
      )}
    </div>
  );


  return (
    <div className="flex h-screen" style={{ backgroundColor: 'var(--c-bg)' }}>
      <style>{`
        .sidebar-scroll::-webkit-scrollbar { width: 0; }
        .sidebar-scroll { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>
      {!embedShell && <CommandPalette />}
      {/* Desktop collapsed-by-default rail. Sits in normal flex flow so
          expanding from 52 → 210 PUSHES the content area right with the
          same 180ms width transition (flex sizing animates smoothly).
          Opens and closes only on the small › / ‹ tab sitting on the rail's
          right edge, halfway down. The wrapper does not clip, so the tab can
          straddle the edge; the rail content inside it does clip. */}
      {!embedShell && (
        <div
          className="hidden md:flex flex-col flex-shrink-0 relative z-30"
          style={{
            width: railExpanded ? RAIL_W_EXPANDED : RAIL_W_COLLAPSED,
            transition: RAIL_TRANSITION,
          }}
        >
          <div className="h-full overflow-hidden">
            <SidebarContent expanded={railExpanded} />
          </div>
          <button
            type="button"
            onClick={toggleRail}
            data-testid="rail-toggle"
            aria-label={railExpanded ? 'Collapse menu' : 'Expand menu'}
            aria-expanded={railExpanded}
            title={railExpanded ? 'Collapse menu' : 'Expand menu'}
            className="absolute top-1/2 -translate-y-1/2 -right-2.5 h-5 w-5 rounded-full flex items-center justify-center bg-white shadow-sm hover:brightness-95 transition"
            style={{ color: 'var(--c-rail)', border: '1.5px solid var(--c-rail)' }}
          >
            {railExpanded
              ? <ChevronLeft className="h-3 w-3" strokeWidth={2.25} />
              : <ChevronRight className="h-3 w-3" strokeWidth={2.25} />}
          </button>
        </div>
      )}

      {/* Mobile sidebar overlay — always fully expanded on mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/20" onClick={() => setMobileOpen(false)} />
          <div className="fixed left-0 top-0 bottom-0 flex flex-col" style={{ width: RAIL_W_EXPANDED }}>
            <SidebarContent expanded={true} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {impersonation && <ImpersonationBanner />}
        {/* Header — no card. Facility name 22px/500, location/date
            subline 12px tertiary; right: facility switch, search, bell,
            avatar (32px circles, 0.5px border) per CANONICAL spec. */}
        {!embedShell && (
        <header style={{ backgroundColor: 'var(--c-bg)' }}>
          <div className="px-4 md:px-8 pt-5 pb-3 flex justify-between items-start">
            <div className="flex items-start gap-3 min-w-0 flex-1 basis-0 mr-2">
              <button
                onClick={() => setMobileOpen(true)}
                className="p-1.5 -ml-1 rounded-md md:hidden flex-shrink-0"
                style={{ color: 'var(--c-ink3)' }}
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <h2
                  className="truncate"
                  style={{ fontSize: '22px', fontWeight: 500, letterSpacing: '-0.3px', color: 'var(--c-ink)', lineHeight: 1.2 }}
                >
                  {isPlatformUser() && !impersonation
                    ? 'mySMS360 Platform'
                    : impersonation
                    ? impersonation.organization_name
                    : currentFacility?.name || 'mySMS360'}
                </h2>
                <p className="mt-1 truncate" style={{ fontSize: '12px', color: 'var(--c-ink3)' }}>
                  {headerSubline}
                </p>
                {facilityExternalId && (!isPlatformUser() || impersonation) && (
                  <p
                    title="Include both IDs on support tickets"
                    aria-label={
                      portfolioExternalId
                        ? `Portfolio ID ${portfolioExternalId}, Facility ID ${facilityExternalId}`
                        : `Facility ID ${facilityExternalId}`
                    }
                    className="mt-0.5 select-text"
                    style={{
                      fontSize: '11px',
                      color: 'var(--c-ink3)',
                    }}
                  >
                    {portfolioExternalId && (
                      <>
                        Portfolio ID :{' '}
                        <span
                          style={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            letterSpacing: '0.4px',
                          }}
                        >
                          {portfolioExternalId}
                        </span>
                        <span style={{ margin: '0 6px', opacity: 0.5 }}>·</span>
                      </>
                    )}
                    Facility ID :{' '}
                    <span
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        letterSpacing: '0.4px',
                      }}
                    >
                      {facilityExternalId}
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0 pl-2">
              <Link
                to="/help"
                title="Help center"
                aria-label="Help center"
                className="p-1.5 rounded-md transition-colors"
                style={{ color: 'var(--c-ink3)' }}
              >
                <HelpCircle className="h-4 w-4" />
              </Link>
              {/* ALWAYS reachable. This was `hidden sm:block`, so on a phone
                  in PORTRAIT (below 640px) the facility switcher disappeared
                  entirely — rotating to landscape crossed the breakpoint and
                  brought it back, which is why it looked like a rotation bug.
                  An operator with several facilities could not change
                  location at all on a phone held normally. On narrow screens
                  the button collapses to just the chevron so it costs almost
                  no width. */}
              {(!isPlatformUser() || impersonation) && facilities.length > 1 && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setFacilityPickerOpen((o) => !o)}
                    onBlur={() => setTimeout(() => setFacilityPickerOpen(false), 150)}
                    aria-label="Switch facility"
                    aria-haspopup="listbox"
                    aria-expanded={facilityPickerOpen}
                    className="flex items-center gap-1 rounded-lg px-2 sm:pl-3 sm:pr-2 py-1.5 text-[12px] focus:outline-none whitespace-nowrap"
                    style={{ backgroundColor: 'var(--c-surface)', border: '0.5px solid var(--c-border)', color: 'var(--c-ink2)' }}
                  >
                    {/* Name only where there's room; the chevron alone is the
                        affordance on a phone. */}
                    <span className="hidden sm:inline">
                      {currentFacility?.name || 'Select facility'}
                    </span>
                    <Building2 className="h-4 w-4 sm:hidden" style={{ color: 'var(--c-ink2)' }} />
                    <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--c-ink3)' }} />
                  </button>
                  {facilityPickerOpen && (
                    <ul
                      role="listbox"
                      // Anchor LEFT on a phone, RIGHT from sm up. The button
                      // sits near the left edge on a narrow header, so
                      // right-anchoring grew the panel leftward and off the
                      // screen (measured x=-149..107 on a 390px viewport —
                      // the facility names were unreadable). Width is
                      // viewport-relative so it never exceeds the screen.
                      className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-50 w-[min(16rem,calc(100vw-1.5rem))] rounded-lg shadow-lg overflow-hidden"
                      style={{ backgroundColor: 'var(--c-surface)', border: '0.5px solid var(--c-border)' }}
                    >
                      {facilities.map((f) => {
                        const isCurrent = f._id === currentFacility?._id;
                        return (
                          <li key={f._id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={isCurrent}
                              onMouseDown={(e) => {
                                // onMouseDown not onClick: fires BEFORE the
                                // button's onBlur, so the picker hasn't yet
                                // closed when we run switchFacility.
                                e.preventDefault();
                                switchFacility(f._id);
                                setFacilityPickerOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 text-[12px] hover:bg-slate-50 transition-colors"
                              style={{
                                color: isCurrent ? 'var(--c-brand)' : 'var(--c-ink2)',
                                fontWeight: isCurrent ? 600 : 400,
                              }}
                            >
                              {f.name}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
              <button
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle light or dark theme"
                className="flex items-center gap-1.5 h-8 px-2 sm:px-3 rounded-full transition-colors"
                style={{ backgroundColor: 'var(--c-surface)', border: '0.5px solid var(--c-border)' }}
              >
                {isDark
                  ? <Sun className="h-3.5 w-3.5" style={{ color: 'var(--c-ink2)' }} />
                  : <Moon className="h-3.5 w-3.5" style={{ color: 'var(--c-ink2)' }} />}
                <span
                  className="hidden sm:inline"
                  style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-ink2)' }}
                >
                  {isDark ? 'Light mode' : 'Dark mode'}
                </span>
              </button>
              {/* Brand color trio — switches --c-brand app-wide via
                  html[data-brand="..."]. Sidebar + every primary button,
                  link, focus ring, and active-row tint flips together. */}
              <div className="hidden sm:flex items-center gap-1.5">
                {VALID_BRAND_KEYS.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setBrand(b)}
                    title={`${b[0].toUpperCase() + b.slice(1)} theme (${BRAND_SWATCHES[b]})`}
                    aria-label={`Switch to ${b} theme ${BRAND_SWATCHES[b]}`}
                    aria-pressed={brand === b}
                    className={`h-3.5 w-3.5 rounded-full ring-offset-1 transition ${
                      brand === b
                        ? 'ring-2 ring-slate-400'
                        : 'hover:ring-1 hover:ring-slate-300'
                    }`}
                    style={{ backgroundColor: BRAND_SWATCHES[b] }}
                  />
                ))}
              </div>
              {user && (
                <>
                  <button
                    onClick={handleLogout}
                    title="Logout"
                    aria-label="Logout"
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--c-surface)', border: '0.5px solid var(--c-border)' }}
                  >
                    <LogOut className="h-3.5 w-3.5" style={{ color: 'var(--c-ink2)' }} />
                  </button>
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--c-bg)', border: '0.5px solid var(--c-border)' }}
                    title={`${user.full_name} · ${displayRoleLabel}`}
                  >
                    <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--c-ink2)' }}>
                      {(user.full_name || 'U').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
        )}

        {/* Page content. min-h-0 lets a child with h-full (Dashboard)
            constrain itself to the viewport; scrollable pages still
            scroll via their own overflow. */}
        <main className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto" style={{ backgroundColor: 'var(--c-bg)' }}>
          {/* 1600px, not max-w-7xl (1280): the list tables carry 11 columns
              since Created/Updated landed, and 1280 forced a horizontal
              scroll on a normal laptop. Still capped so text lines on a
              wide monitor don't run to unreadable lengths. */}
          <div className={`max-w-[1600px] mx-auto px-4 md:px-8 pb-6 h-full${embedShell ? ' pt-4' : ''}`}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
